/**
 * Core types for template processing (analysis and rewrite)
 */

/** Processing operation types */
export type ProcessingType = 'analysis' | 'rewrite';

/** Stage status types */
export type StageStatus = 'pending' | 'in_progress' | 'completed' | 'error' | 'error_continuing' | 'interrupted';

/** Connection status types */
export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting' | 'connecting' | 'error';

/** Base stage state interface */
export interface BaseStageState {
  id: string;
  name: string;
  status: StageStatus;
  progress: number;
  messages: StageMessage[];
  isExpanded: boolean;
  details?: Record<string, unknown>;
}

/** Analysis-specific stage state */
export interface AnalysisStageState extends BaseStageState {
  processingType: 'analysis';
  details?: {
    current_bias?: string;
    tokens_processed?: number;
    content_size?: number;
  };
}

/** Rewrite-specific stage state */
export interface RewriteStageState extends BaseStageState {
  processingType: 'rewrite';
  details?: {
    variation_number?: number;
    total_variations?: number;
    current_bias?: string;
  };
}

/** Union type for stage states */
export type StageState = AnalysisStageState | RewriteStageState;

/** Stage message interface */
export interface StageMessage {
  id: string;
  timestamp: number;
  type: 'info' | 'warning' | 'error';
  content: string;
  details?: Record<string, unknown>;
}

/** Processing state interface */
export interface ProcessingState {
  isProcessing: boolean;
  processingType: ProcessingType;
  stages: StageState[];
  currentStage?: string;
  result?: AnalysisResult | RewriteResult;
  connectionStatus: ConnectionStatus;
}

/** Analysis result interface */
export interface AnalysisResult {
  sender_bias: string;
  content_biases: string[];
  stats?: {
    total_tokens: number;
    content_size: number;
    duration_ms: number;
  };
}

/** Rewrite result interface */
export interface RewriteResult {
  variations: {
    from_address: string;
    subject: string;
    body: string;
  }[];
  stats?: {
    total_variations: number;
    valid_variations: number;
    duration_ms: number;
  };
}

/** Processing context value interface */
export interface ProcessingContextValue {
  state: ProcessingState;
  dispatch: React.Dispatch<ProcessingAction>;
}

/** Processing action types */
export type ProcessingAction =
  | { type: 'START_PROCESSING'; payload: { processingType: ProcessingType } }
  | { type: 'UPDATE_STAGE'; payload: { stageId: string; updates: Partial<StageState> } }
  | { type: 'ADD_MESSAGE'; payload: { stageId: string; message: StageMessage } }
  | { type: 'SET_RESULT'; payload: { result: AnalysisResult | RewriteResult } }
  | { type: 'SET_CONNECTION_STATUS'; payload: { status: ConnectionStatus } }
  | { type: 'TOGGLE_STAGE'; payload: { stageIndex: number } }
  | { type: 'RESET_STATE' };

/** Stage info interface */
export interface StageInfo {
  name: string;
  description: string;
}

/** Stage info mapping */
export const STAGE_INFO: Record<string, StageInfo> = {
  AI_PROCESSING: {
    name: 'AI Processing',
    description: 'Processing template content',
  },
  VALIDATION: {
    name: 'Validation',
    description: 'Validating generated content',
  },
  // Add more stages as needed
};

/** Get stage info helper function */
export function getStageInfo(stageName: string): StageInfo {
  const knownStage = STAGE_INFO[stageName];
  if (knownStage) {
    return knownStage;
  }

  // Handle unknown stages gracefully
  const displayName = stageName.toLowerCase().replace(/_/g, ' ');
  return {
    name: displayName,
    description: `Processing ${displayName}`,
  };
} 